/**
 * Sidebar — always-dark navy chrome with grouped navigation.
 * Active item: a single "liquid" indicator (white fill + bronze left accent)
 * that springs between rows instead of appearing per-row. Collapsible to icons.
 *
 * Liquid mechanics: all rows are direct children of one column and report
 * their layout (y inside that column), and one absolutely-positioned pill
 * animates `y`/`height` to the active row with the `bouncy` spring — so
 * selection visibly flows from the old item to the new one (honors reduce-motion).
 */
import { useState } from 'react'
import { Image, type LayoutChangeEvent } from 'react-native'
import { usePathname, useRouter } from 'expo-router'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, useUiPrefs } from '../i18n'
import { useResponsive } from '../lib/responsive'
import { useMyTasks } from '../features/edo/useEdo'
import { CHROME } from '../theme/tones'
import { accentChrome } from '../theme/accents'
import { Icon } from '../ui'
import { pressable } from '../ui/primitives'
import { prefersReducedMotion } from '../ui/motion'
import { NAV_BOTTOM, NAV_LIFE, NAV_MAIN, NAV_WORK, type NavItem, type NavRoute } from './nav'

const asMark = require('../../assets/brand/as-mark.png')

/** Not-ready («Скоро») items sink to the bottom of their nav group (stable). */
const soonLast = (items: NavItem[]) => [...items.filter((i) => !i.soon), ...items.filter((i) => i.soon)]

function NavRow({
  item,
  active,
  collapsed,
  onPress,
  onLayout,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
  onPress: () => void
  onLayout?: (e: LayoutChangeEvent) => void
}) {
  const { l } = useApp()
  const label = l('nav.' + item.key)
  // "soon" sections aren't built yet: the row is non-interactive, dimmed, and
  // tagged «Скоро». Navigation is also blocked at the layout (see nav.ts).
  const soon = !!item.soon
  const on = active && !soon
  return (
    <XStack
      {...pressable}
      // soon-пункты не роутят: не отдаём им tag="button" (PressCard-паттерн) —
      // не выдаём мёртвый ряд за интерактивный элемент.
      tag={soon ? undefined : pressable.tag}
      onPress={soon ? undefined : onPress}
      onLayout={onLayout}
      cursor={soon ? 'default' : 'pointer'}
      animation="quick"
      alignItems="center"
      justifyContent={collapsed ? 'center' : 'flex-start'}
      gap={11}
      paddingVertical={9}
      paddingHorizontal={collapsed ? 0 : 12}
      borderRadius={5}
      position="relative"
      // The active fill/accent is drawn by the shared LiquidIndicator (see
      // Sidebar body) so it can flow between rows; rows stay transparent.
      hoverStyle={soon || on ? {} : { backgroundColor: CHROME.hover }}
      pressStyle={soon ? {} : { scale: 0.97 }}
      // soon-пункты не роутят (onPress выше уже undefined) — не выдаём их за
      // ссылки для скринридера.
      role={soon ? undefined : 'link'}
      aria-label={soon ? undefined : label}
      aria-current={on ? 'page' : undefined}
    >
      <Stack position="relative" opacity={soon ? 0.5 : 1}>
        <Icon name={item.icon} size={17} color={on ? CHROME.bronze : CHROME.iconIdle} />
        {collapsed && item.badge && !soon ? (
          <Stack position="absolute" top={-3} right={-3} width={7} height={7} borderRadius={999} backgroundColor={CHROME.bronzeSolid} />
        ) : null}
        {collapsed && soon ? (
          <Stack position="absolute" top={-3} right={-3} width={6} height={6} borderRadius={999} backgroundColor={CHROME.badgeIdle} />
        ) : null}
      </Stack>
      {!collapsed && (
        <Text flex={1} fontSize={14} fontWeight={on ? '600' : '500'} color={on ? CHROME.textActive : CHROME.textIdle} opacity={soon ? 0.6 : 1}>
          {label}
        </Text>
      )}
      {!collapsed && soon ? (
        <XStack
          backgroundColor={CHROME.badgeIdle}
          borderRadius={999}
          paddingHorizontal={8}
          paddingVertical={2}
          alignItems="center"
          justifyContent="center"
        >
          <Text fontSize={10} fontWeight="700" letterSpacing={0.3} color={CHROME.textIdle}>
            {l('c.soon')}
          </Text>
        </XStack>
      ) : !collapsed && item.badge ? (
        <XStack
          backgroundColor={CHROME.bronzeSolid}
          borderRadius={999}
          paddingHorizontal={7}
          paddingVertical={1}
          minWidth={20}
          alignItems="center"
          justifyContent="center"
        >
          <Text fontSize={11} fontWeight="700" color="#fff">
            {item.badge}
          </Text>
        </XStack>
      ) : null}
    </XStack>
  )
}

function SectionLabel({ k, collapsed }: { k: string; collapsed: boolean }) {
  const { l } = useApp()
  if (collapsed) return <Stack height={1} backgroundColor={CHROME.divider} marginVertical={9} marginHorizontal={8} />
  return (
    <Text paddingHorizontal={12} paddingTop={14} paddingBottom={6} fontSize={10} fontWeight="700" letterSpacing={1} color={CHROME.section}>
      {l(k)}
    </Text>
  )
}

/** Row geometry inside the nav column. */
interface RowRect {
  y: number
  h: number
}

/**
 * The liquid selection pill — one shared element that springs (`bouncy`)
 * between rows. Height animates with it, so long/short rows morph fluidly.
 */
function LiquidIndicator({ y, h }: { y: number; h: number }) {
  const anim = prefersReducedMotion() ? undefined : ('bouncy' as const)
  return (
    <Stack
      position="absolute"
      left={0}
      right={0}
      top={0}
      y={y}
      height={h}
      borderRadius={5}
      backgroundColor="rgba(255,255,255,0.10)"
      animation={anim}
      enterStyle={{ opacity: 0, scale: 0.9 }}
      opacity={1}
      scale={1}
      pointerEvents="none"
    >
      <Stack
        position="absolute"
        left={0}
        top={6}
        bottom={6}
        width={3}
        borderRadius={2}
        backgroundColor={CHROME.bronze}
      />
    </Stack>
  )
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const router = useRouter()
  const pathname = usePathname()
  const { lang } = useApp()
  const { accent } = useUiPrefs()
  const chrome = accentChrome(accent)
  const { isPhone } = useResponsive()
  // Live nav badges (no more hardcoded mock counts). «Процессы» = my open EDO
  // approval tasks — same source (GET /edo/tasks) as the screen's «Все» tab.
  // Shared TanStack query, so this just reuses the cache. 0 → no badge.
  const { data: edoTasks } = useMyTasks()
  // On phone the sidebar is a drawer: collapsing to icons makes no sense, and
  // the user dismisses it by tapping the scrim. So the collapse handle is
  // desktop/tablet-only. `collapsed` stays false on phone.
  const [collapsed, setCollapsed] = useState(false)

  // ── liquid indicator geometry ─────────────────────────────────────────────
  // Строки — прямые дети одной колонки, их y из onLayout уже и есть смещение
  // пилюли. Обёрток-групп с onLayout нет намеренно: на вебе Tamagui кэширует
  // размеры общим кэшем, и замер строки записывает туда новый размер группы как
  // «родителя» — группа потом не видит изменений и onLayout не шлёт. Так после
  // сворачивания пилюля оставалась на старом месте (ниже на разницу высоты
  // подписи раздела и разделителя, замечание 30.10.2026).
  const [rowRects, setRowRects] = useState<Record<string, RowRect>>({})

  const reportRow = (key: string) => (e: LayoutChangeEvent) => {
    const { y, height } = e.nativeEvent.layout
    setRowRects((prev) => {
      const cur = prev[key]
      if (cur && cur.y === y && cur.h === height) return prev
      return { ...prev, [key]: { y, h: height } }
    })
  }

  const go = (route: NavRoute) => {
    router.push(route)
    onNavigate?.()
  }
  const isActive = (route: string) => (route === '/' ? pathname === '/' : pathname.startsWith(route))

  // key → live count. Falls back to the item's own badge when not tracked here.
  const liveBadges: Record<string, number> = { processes: edoTasks?.length ?? 0 }

  const renderRows = (items: NavItem[]) =>
    soonLast(items.filter((it) => !it.hidden)).map((it) => {
      const badge = liveBadges[it.key] ?? it.badge
      return (
        <NavRow
          key={it.key}
          item={badge ? { ...it, badge } : it}
          active={isActive(it.route)}
          collapsed={collapsed}
          onPress={() => go(it.route)}
          onLayout={reportRow(it.key)}
        />
      )
    })

  // Active nav item (non-soon) → target rect for the liquid pill.
  const activeItem = [...NAV_MAIN, ...NAV_WORK, ...NAV_LIFE, ...NAV_BOTTOM].find(
    (it) => !it.soon && !it.hidden && isActive(it.route),
  )
  const activeRect = activeItem ? rowRects[activeItem.key] : undefined

  // ponytail: RU/EN only — AZ/KA fall back to RU (same convention as tr()'s
  // ka handling). No dedicated i18n key: this agent's file scope is Sidebar/
  // BottomNav/TopBar only, not src/i18n/strings.ts. Add `nav.collapse` /
  // `nav.expand` there for full AZ/KA if this needs to be exact.
  const collapseLabel = lang === 'en' ? (collapsed ? 'Expand menu' : 'Collapse menu') : collapsed ? 'Развернуть меню' : 'Свернуть меню'

  return (
    <YStack
      // Rail width animates on collapse/expand (single element, so the layout
      // pass is cheap; content reflows with it).
      animation="quicker"
      width={collapsed ? 68 : 236}
      flexShrink={0}
      height="100%"
      backgroundColor={chrome.bg}
      padding={collapsed ? 10 : 14}
      position="relative"
    >
      {/* collapse handle — desktop/tablet only; on phone the drawer is dismissed
          by tapping the scrim, so no collapse arrow is shown */}
      {!isPhone && (
        <XStack
          {...pressable}
          onPress={(e) => {
            setCollapsed((c) => !c)
            // Нажатие мышью/пальцем/пером (detail > 0) — снимаем фокус: иначе после сворачивания
            // на ручке висело кольцо фокуса (замечание 29.10.2026). С клавиатуры (Enter/Space —
            // detail 0) фокус остаётся, кольцо нужно для навигации без мыши.
            const ev = e?.nativeEvent as unknown as { detail?: number } | undefined
            if (ev?.detail) (e?.currentTarget as unknown as HTMLElement | null)?.blur?.()
          }}
          cursor="pointer"
          position="absolute"
          // centred on the «Главная» row in both rails — the collapsed rail has
          // tighter outer padding (10 vs 14) and a textless (shorter) row, so it
          // sits ~5px higher than the expanded one.
          top={collapsed ? 66 : 71}
          right={-14}
          zIndex={40}
          width={28}
          height={28}
          borderRadius={999}
          backgroundColor={CHROME.bronzeSolid}
          borderWidth={2}
          borderColor={chrome.bgDeep}
          alignItems="center"
          justifyContent="center"
          animation="quicker"
          hoverStyle={{ scale: 1.12 }}
          pressStyle={{ scale: 0.9 }}
          role="button"
          aria-label={collapseLabel}
        >
          <Stack animation="quick" rotate={collapsed ? '180deg' : '0deg'}>
            <Icon name="chevron-left" size={15} color="#fff" />
          </Stack>
        </XStack>
      )}

      {/* logo — taps through to the dashboard (closes the drawer on phone via go()) */}
      <XStack
        onPress={() => go('/home')}
        cursor="pointer"
        hoverStyle={{ opacity: 0.85 }}
        flexDirection={collapsed ? 'column' : 'row'}
        alignItems="center"
        gap={collapsed ? 10 : 11}
        paddingVertical={6}
        paddingBottom={10}
        paddingHorizontal={collapsed ? 0 : 6}
      >
        <Stack width={30} height={30} borderRadius={5} backgroundColor="#fff" alignItems="center" justifyContent="center">
          <Image source={asMark} style={{ width: 21, height: 21, resizeMode: 'contain' }} />
        </Stack>
        {!collapsed && (
          <Text flex={1} fontSize={16} fontWeight="700" letterSpacing={-0.2} color="#fff">
            AS Life
          </Text>
        )}
      </XStack>

      {/* single relative column: all rows (incl. the bottom block) share one
          coordinate space so the liquid pill can travel anywhere, incl. into Настройки */}
      <YStack flex={1} gap={2} paddingTop={6} position="relative">
        {activeRect != null && <LiquidIndicator y={activeRect.y} h={activeRect.h} />}
        {renderRows(NAV_MAIN)}
        <SectionLabel k="nav.section.work" collapsed={collapsed} />
        {renderRows(NAV_WORK)}
        <SectionLabel k="nav.section.life" collapsed={collapsed} />
        {renderRows(NAV_LIFE)}
        <Stack flex={1} />
        {/* черта над нижним блоком: 1px + 8 + gap 2 = прежние border + paddingTop 10 */}
        <Stack height={1} marginBottom={8} backgroundColor={CHROME.divider} />
        {renderRows(NAV_BOTTOM)}
      </YStack>
    </YStack>
  )
}
